/**
 * Route layout: each team's map as a route from A to B.
 *
 * One horizontal lane per scenario root (a team's starting point), stacked top
 * to bottom by title. In each lane the root sits at the left edge, the goal
 * (`type 'tavoite'`) at the right edge — every lane's goal in the same column.
 * Everything else, milestones (`type 'virstanpylvas'`) included, is a node like
 * any other: left to right by IOOI kind (input → output → outcome → impact) and
 * within a kind by tree depth, in bands above and below the root's row. A
 * milestone chain (town → m1 → m2, written in date order) reads left to right.
 * Every connector runs clear of every other card (route.test.js checks it).
 *
 * The middle columns are shared by every lane, so the same kind lines up
 * across teams: reading down a column is reading what the teams have in common
 * at that point of the route. Cross-team links (second parents) are drawn by
 * the renderer like any other link; they simply run between lanes here.
 *
 * Same contract as organicTree: positions are card centres, arrows carry the
 * same path and `blockerColumn` shape, and `blockedEdges`, `blockerHeights`
 * and `extraDepth` mean the same thing. See specs/workshop-modules/impact_path.md
 * → "Route layout".
 */
import { generateArrowPath } from './organicTree';
import { orthPath } from '../orthogonalRoute';

/**
 * A chain edge at right angles, readable as an org chart: out of the parent's
 * (+) button, a turn in the gap right after the parent, up or down, then along
 * the child's own row into its left edge. A parent's children fan out from one
 * vertical beside the parent, so a reader follows each line back to the card it
 * leaves. The vertical sits in the gutter, never at "halfway", which on a long
 * edge fell inside another card's column; the run along the child's row is
 * clear because each subtree keeps its own band of rows. Cross-team links stay
 * freeform (CrossLinkLayer), in the background. Same row: a straight line.
 */
export function routeTurn(from, to, config) {
  const startX = from.x + config.cardWidth / 2;
  const endX = to.x - config.cardWidth / 2;
  const gap = config.horizontalGap ?? DEFAULT_CONFIG.horizontalGap;
  const room = Math.min(gap / 2, (endX - startX) / 2);
  const turnX = to.turnBefore ? endX - room : startX + room;
  return { startX, endX, turnX, straight: Math.abs(from.y - to.y) < 1 || endX <= startX + 60 };
}

export function routeArrowPath(from, to, config) {
  const { startX, endX, turnX, straight } = routeTurn(from, to, config);
  if (straight) return generateArrowPath(from, to, config);
  return orthPath([
    { x: startX, y: from.y }, { x: turnX, y: from.y }, { x: turnX, y: to.y }, { x: endX, y: to.y },
  ]);
}

const DEFAULT_CONFIG = {
  cardWidth: 280,
  cardHeight: 120,
  horizontalGap: 200,
  verticalGap: 40,
  rootX: 50,
  rootY: 300,
};

export const GOAL_TYPE = 'tavoite';
export const MILESTONE_TYPE = 'virstanpylvas';

const KIND_RANK = { input: 0, output: 1, outcome: 2, impact: 3 };

const isLaneRoot = (n) => n.is_scenario_starter === true || n.type === 'scenario_root';
const primaryParent = (n) => n.parentIds?.[0] || null;

/** Which lane each node belongs to: the lane root its primary-parent chain reaches. */
function assignLanes(nodes, nodeMap) {
  const mapRoot = nodes.find((n) => n.isRoot) || null;
  // A lane root is a scenario root, a child of the workshop root, or anything
  // with nothing above it (a seam root, an orphaned chain).
  const laneRoots = nodes.filter((n) => !n.isRoot && (
    isLaneRoot(n)
    || !primaryParent(n)
    || !nodeMap[primaryParent(n)]
    || (mapRoot && primaryParent(n) === mapRoot.id)
  ));
  const laneRootIds = new Set(laneRoots.map((n) => n.id));
  const laneOf = {};
  nodes.forEach((n) => {
    if (n.isRoot) return;
    let cur = n;
    let guard = 0;
    while (cur && !laneRootIds.has(cur.id) && guard < 500) {
      cur = nodeMap[primaryParent(cur)];
      guard += 1;
    }
    laneOf[n.id] = cur && laneRootIds.has(cur.id) ? cur.id : null;
  });
  laneRoots.sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'fi'));
  return { mapRoot, laneRoots, laneOf };
}

/**
 * Column key per middle node: kind rank first, tree depth second. A node with
 * no IOOI kind of its own (a blocker, a negative consequence, an untyped card)
 * takes its parent's, so it lands in the column right after the node it hangs
 * from. A blocked edge reserves one column for the blocker card, as in
 * organicTree.
 */
function columnKeys(laneRoot, nodeMap, pinned, config) {
  const keys = {};
  const blockerKeys = {};
  // Depth counts steps within the kind, not from the root: a chain of five
  // outputs after two inputs is output columns 0–4, not 2–6. Counted from the
  // root, every depth of every kind became a column of its own and TKI's nine
  // teams needed fifty of them.
  const walk = (node, depth, rank) => {
    (node.children || []).forEach((childId) => {
      const child = nodeMap[childId];
      if (!child || child.isRoot || isLaneRoot(child)) return;
      const blocked = config.blockedEdges?.has(`${node.id}->${childId}`);
      const kind = child.node_analysis?.chainKind;
      const r = kind in KIND_RANK ? KIND_RANK[kind] : rank;
      const sameKind = node !== laneRoot && r === rank;
      const d = (sameKind ? depth + 1 : 0) + (blocked ? 1 : 0) + (config.extraDepth?.[childId] || 0);
      if (!pinned.has(childId)) {
        keys[childId] = r * 1000 + d;
        if (blocked) blockerKeys[`${node.id}->${childId}`] = r * 1000 + d - 1;
      }
      walk(child, d, r);
    });
  };
  walk(laneRoot, 0, 0);
  return { keys, blockerKeys };
}

/** Height of a node's band: its own row (a stacked blocker card can make it taller) or its children's. */
function bandHeight(id, bandChildren, config, memo) {
  if (memo[id] != null) return memo[id];
  const own = Math.max(config.cardHeight, config.blockerHeights?.get(id) || 0);
  const kids = bandChildren[id] || [];
  const sum = kids.reduce((s, k) => s + bandHeight(k, bandChildren, config, memo), 0)
    + Math.max(0, kids.length - 1) * config.verticalGap;
  memo[id] = Math.max(own, sum);
  return memo[id];
}

function placeBand(id, top, bandChildren, config, memo, ys) {
  const h = bandHeight(id, bandChildren, config, memo);
  ys[id] = top + h / 2;
  let y = top;
  (bandChildren[id] || []).forEach((k) => {
    placeBand(k, y, bandChildren, config, memo, ys);
    y += bandHeight(k, bandChildren, config, memo) + config.verticalGap;
  });
}

export function layoutRoute(nodes, customConfig = {}) {
  const config = { ...DEFAULT_CONFIG, ...customConfig };
  const step = config.cardWidth + config.horizontalGap;
  const nodeMap = {};
  nodes.forEach((n) => { nodeMap[n.id] = n; });

  const { mapRoot, laneRoots, laneOf } = assignLanes(nodes, nodeMap);
  if (!laneRoots.length) return { positions: {}, arrows: [], lanes: [] };

  const lanes = laneRoots.map((root) => {
    const members = nodes.filter((n) => laneOf[n.id] === root.id && n.id !== root.id);
    // Only goals are pinned (the right-hand column). A milestone is a node like
    // the others: its chain (town → m1 → m2 …, by date) gets columns and a band.
    const goals = members.filter((n) => n.type === GOAL_TYPE);
    const pinned = new Set(goals.map((n) => n.id));
    return { root, members, goals, pinned };
  });

  // Shared middle columns: every key any lane uses (and every column a
  // blocker card needs), in order.
  const allKeys = new Set();
  lanes.forEach((lane) => {
    const { keys, blockerKeys } = columnKeys(lane.root, nodeMap, lane.pinned, config);
    lane.keys = keys;
    lane.blockerKeys = blockerKeys;
    Object.values(keys).forEach((k) => allKeys.add(k));
    Object.values(blockerKeys).forEach((k) => allKeys.add(k));
  });
  const ordered = [...allKeys].sort((a, b) => a - b);
  const columnOf = new Map(ordered.map((k, i) => [k, i + 1]));
  const middleColumns = Math.max(ordered.length, 1);
  const xOfColumn = (c) => config.rootX + c * step;
  const goalX = xOfColumn(middleColumns + 1);

  const positions = {};
  const blockerAt = {};
  const laneBoxes = [];
  let laneTop = config.rootY;
  const row = config.cardHeight;
  const gap = config.verticalGap;

  lanes.forEach((lane) => {
    // Bands: the tree minus the pinned cards. A node whose parent is pinned
    // (hangs off a milestone or the goal) starts a band of its own.
    const bandChildren = {};
    const tops = [];
    lane.members.forEach((n) => {
      if (lane.pinned.has(n.id)) return;
      const p = primaryParent(n);
      if (p && p !== lane.root.id && nodeMap[p] && !lane.pinned.has(p) && laneOf[p] === lane.root.id) {
        (bandChildren[p] = bandChildren[p] || []).push(n.id);
      } else {
        tops.push(n.id);
      }
    });
    // Keep the tree's own sibling order.
    Object.keys(bandChildren).forEach((p) => {
      const order = nodeMap[p].children || [];
      bandChildren[p].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    });
    const memo = {};
    const heights = tops.map((id) => bandHeight(id, bandChildren, config, memo));
    const total = heights.reduce((s, h) => s + h + gap, 0);
    const above = [];
    const below = [];
    let aboveH = 0;
    tops.forEach((id, i) => {
      if (aboveH < total / 2 - heights[i] / 2) { above.push(id); aboveH += heights[i] + gap; } else below.push(id);
    });
    const belowH = below.reduce((s, id) => s + bandHeight(id, bandChildren, config, memo) + gap, 0);
    const goalsH = lane.goals.length * row + Math.max(0, lane.goals.length - 1) * gap;

    const topExtent = Math.max(row / 2, goalsH / 2, aboveH ? aboveH + row / 2 : 0);
    const bottomExtent = Math.max(row / 2, goalsH / 2, belowH ? belowH + row / 2 : 0);
    const centreY = laneTop + topExtent;

    const ys = {};
    let y = centreY - row / 2 - aboveH;
    above.forEach((id) => {
      placeBand(id, y, bandChildren, config, memo, ys);
      y += bandHeight(id, bandChildren, config, memo) + gap;
    });
    y = centreY + row / 2 + gap;
    below.forEach((id) => {
      placeBand(id, y, bandChildren, config, memo, ys);
      y += bandHeight(id, bandChildren, config, memo) + gap;
    });

    positions[lane.root.id] = { x: xOfColumn(0), y: centreY };
    Object.entries(ys).forEach(([id, yy]) => {
      const col = columnOf.get(lane.keys[id]) || 1;
      positions[id] = { x: xOfColumn(col), y: yy };
    });
    Object.entries(lane.blockerKeys).forEach(([edge, k]) => {
      blockerAt[edge] = xOfColumn(columnOf.get(k));
    });
    // A goal's connector arrives along its source's row and turns just before
    // the goal column (routeTurn): that row is clear, the goal's own is not.
    lane.goals.forEach((g, i) => {
      positions[g.id] = { x: goalX, y: centreY - goalsH / 2 + row / 2 + i * (row + gap), turnBefore: true };
    });

    const bottom = centreY + bottomExtent;
    laneBoxes.push({ rootId: lane.root.id, title: lane.root.title, top: laneTop, centreY, bottom });
    laneTop = bottom + gap * 4;
  });

  if (mapRoot) {
    const mid = (laneBoxes[0].top + laneBoxes[laneBoxes.length - 1].bottom) / 2;
    positions[mapRoot.id] = { x: xOfColumn(-1), y: mid };
  }

  const arrows = [];
  nodes.forEach((node) => {
    (node.parentIds || []).forEach((parentId) => {
      const from = positions[parentId];
      const to = positions[node.id];
      if (!from || !to) return;
      const edge = `${parentId}->${node.id}`;
      arrows.push({
        from: parentId,
        to: node.id,
        path: routeArrowPath(from, to, config),
        ...(blockerAt[edge] != null ? { blockerColumn: { x: blockerAt[edge], y: to.y } } : {}),
      });
    });
  });

  return { positions, arrows, lanes: laneBoxes };
}

export default layoutRoute;
